import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import { type AlertColor, Button, Card, Stack } from '@mui/material'
import { type AllFundingRate, type ExchangeFundingRate } from '../../models/funding-rate/funding-rate-types'
import { isEmptyString } from '../../utils/string-utils'
import { isStorageSupported } from '../../utils/storage-utils'
import StackRowLayout from '../../layouts/stack-row-layout'
import SaveSlotSelect from './fr-save-slot-select'
import FRIntervalButtons from './fr-interval-buttons'
import FRToggleAllSymbolsButtons from './fr-toggle-all-symbols-buttons'
import FRAutoCompleteFilterSelect from './fr-auto-complete-filter-select'
import SymbolSwitchList from './fr-symbol-switch-list'
import FundingRateLineChart from './funding-rate-line-chart'
import ToastWithMessage from '../../components/toast/toast-with-message'

const FUNDING_RATE_CHART_LOCAL_STORAGE_LAST_ENABLED_KEY = 'funding-rate-chart-last-enabled-symbols'

const FundingRateChart: React.FC = () => {
  const [frInterval, setFRInterval] = useState<string>('3D')
  const onUpdateFRInterval = (value: string): void => {
    setFRInterval(value)
  }

  const { data, error } = useSWR<AllFundingRate, boolean>(
    frInterval !== '' ? `${process.env.REACT_APP_API_FUNDING_RATE as string}${frInterval}` : null,
    basicFetcher,
    {
      shouldRetryOnError: true,
      refreshInterval: 5000,
    },
  )

  const localStorageSupported = useMemo<boolean>(() => isStorageSupported(), [])

  // Keeps track of the available symbols that can be charted
  const [optionsSymbol, setOptionsSymbol] = useState<string[]>([])

  // enabledSymbols and disabledSymbols must always be updated together
  const [enabledSymbols, setEnabledSymbols] = useState<string[]>([])
  const [disabledSymbols, setDisabledSymbols] = useState<string[]>([])

  useEffect(
    () => {
      const dataToChart: ExchangeFundingRate = (error === true || data === undefined) ? {} : data.data

      const exchanges: string[] = Object.keys(dataToChart)
      const allSymbols: string[] = []

      exchanges.forEach(exchange => {
        const symbols: string[] = Array.from(new Set(dataToChart[exchange].map((item) => item.symbol_name)))
        allSymbols.push(...symbols)
      })

      const sortedSymbols = allSymbols.sort()
      setOptionsSymbol(sortedSymbols)

      if (enabledSymbols.length === 0 && disabledSymbols.length === 0) {
        // If this is the first time the data was loaded
        const lastEnabledSymbolsStr = localStorageSupported ? localStorage.getItem(FUNDING_RATE_CHART_LOCAL_STORAGE_LAST_ENABLED_KEY) : null
        if (lastEnabledSymbolsStr != null) {
          const lastEnabledSymbols: string[] = JSON.parse(lastEnabledSymbolsStr)
          const validLastEnabledSymbols = lastEnabledSymbols.filter(item => sortedSymbols.includes(item))
          setEnabledSymbols(validLastEnabledSymbols)
          setDisabledSymbols(sortedSymbols.filter(item => !validLastEnabledSymbols.includes(item)))
        } else {
          setDisabledSymbols(sortedSymbols)
        }
      } else {
        // If this isn't the first time the data was loaded
        const newEnabledSymbols = enabledSymbols.filter(symbol => sortedSymbols.includes(symbol))
        const newDisabledSymbols = sortedSymbols.filter(symbol => !newEnabledSymbols.includes(symbol))
        setDisabledSymbols(newDisabledSymbols)
        setEnabledSymbols(newEnabledSymbols)
      }
    },
    [frInterval, localStorageSupported, error, data],
  )

  const [filterSymbol, setFilterSymbol] = useState<string>('')

  const symbols = useMemo<string[]>(() => {
    return optionsSymbol
      .filter((key) => (filterSymbol != null) ? key.toLowerCase().includes(filterSymbol.toLowerCase()) : true)
      .sort((key1, key2) => {
        return key1.localeCompare(key2, undefined, { numeric: true })
      })
      .map(key => key)
  }, [optionsSymbol, filterSymbol])

  const saveToLocalStorage = (key: string, value: string, onSuccess: any | undefined = null): void => {
    if (localStorageSupported) {
      try {
        localStorage.setItem(key, value)
        if (onSuccess != null) {
          onSuccess()
        }
      } catch (err) {
        // Handle the case where localStorage is full
        displayToastWithMessage('Local storage is full', 'error')
      }
    }
  }

  const handleSwitch = useCallback((event: React.ChangeEvent<HTMLInputElement>, key: string): void => {
    const isChecked = event.target.checked
    let newEnabledSymbols: string[]
    if (isChecked) {
      newEnabledSymbols = enabledSymbols.concat(key)
      setEnabledSymbols(newEnabledSymbols)
      setDisabledSymbols(disabledSymbols.filter(item => item !== key))
    } else {
      newEnabledSymbols = enabledSymbols.filter(item => item !== key)
      setEnabledSymbols(newEnabledSymbols)
      setDisabledSymbols(disabledSymbols.concat(key))
    }
    saveToLocalStorage(FUNDING_RATE_CHART_LOCAL_STORAGE_LAST_ENABLED_KEY, JSON.stringify(newEnabledSymbols))
  }, [enabledSymbols, disabledSymbols])

  const toggleAllSymbols = useCallback((value: boolean): void => {
    let newEnabledSymbols: string[]
    if (value) {
      newEnabledSymbols = enabledSymbols.concat(disabledSymbols)
      setEnabledSymbols(newEnabledSymbols)
      setDisabledSymbols([])
    } else {
      newEnabledSymbols = []
      setDisabledSymbols(disabledSymbols.concat(enabledSymbols))
      setEnabledSymbols(newEnabledSymbols)
    }
    saveToLocalStorage(FUNDING_RATE_CHART_LOCAL_STORAGE_LAST_ENABLED_KEY, JSON.stringify(newEnabledSymbols))
  }, [enabledSymbols, disabledSymbols])

  const [saveSlot, setSaveSlot] = useState<string | ''>('')
  const onSave = (): void => {
    if (!localStorageSupported) {
      displayToastWithMessage('Local Storage API is not supported', 'warning')
      return
    }
    if (isEmptyString(saveSlot)) {
      displayToastWithMessage('Unable to save as no save slot was selected', 'warning')
      return
    }
    saveToLocalStorage(saveSlot, JSON.stringify(enabledSymbols), () => {
      displayToastWithMessage(`Saved current selection to: ${saveSlot}`, 'success')
    })
  }

  const onLoad = (): void => {
    if (!localStorageSupported) {
      displayToastWithMessage('Local Storage API is not supported', 'warning')
      return
    }
    if (isEmptyString(saveSlot)) {
      displayToastWithMessage('Unable to load as no save slot was selected', 'warning')
      return
    }

    const savedSymbolsStr = localStorage.getItem(saveSlot)

    if (savedSymbolsStr == null || isEmptyString(savedSymbolsStr)) {
      displayToastWithMessage('Unable to load as no data was found for the selected save slot', 'warning')
      return
    }

    const parsedSymbols: string[] = JSON.parse(savedSymbolsStr)
    const validParsedSymbols = parsedSymbols.filter(item => optionsSymbol.includes(item))

    setEnabledSymbols(validParsedSymbols)
    setDisabledSymbols(optionsSymbol.filter(item => !validParsedSymbols.includes(item)))

    saveToLocalStorage(FUNDING_RATE_CHART_LOCAL_STORAGE_LAST_ENABLED_KEY, JSON.stringify(validParsedSymbols))
    displayToastWithMessage(`Loaded selection from: ${saveSlot}`, 'success')
  }

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = (message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }

  return (
    <StackRowLayout height='100%'>
      <Card variant='outlined' sx={{ width: 1, height: 1, padding: 2, borderRadius: 0 }}>
        <Stack direction='row' sx={{ width: 1, height: 1 }}>
          <Stack sx={{ display: 'flex', gap: 1, paddingRight: 1 }}>
            <Stack direction='row' columnGap={1} alignItems='center'>
              <SaveSlotSelect
                localStorageSupported={localStorageSupported}
                saveSlot={saveSlot}
                setSaveSlot={setSaveSlot}
              />

              <Button
                variant='outlined'
                size='small'
                onClick={() => {
                  onSave()
                }}
                disabled={!localStorageSupported}
              >
                Save
              </Button>

              <Button
                variant='outlined'
                size='small'
                onClick={() => {
                  onLoad()
                }}
                disabled={!localStorageSupported}
              >
                Load
              </Button>
            </Stack>

            <FRIntervalButtons frInterval={frInterval} updateFRInterval={onUpdateFRInterval}/>
            <FRToggleAllSymbolsButtons toggleAllSymbols={toggleAllSymbols}/>
            <FRAutoCompleteFilterSelect
              optionsSymbol={optionsSymbol}
              filterSymbol={filterSymbol}
              setFilterSymbol={setFilterSymbol}
            />
            <SymbolSwitchList symbols={symbols} enabledSwitches={enabledSymbols} handleSwitch={handleSwitch}/>
          </Stack>

          <FundingRateLineChart
            frInterval={frInterval}
            chartData={data == null ? {} : data.data}
            enabledSymbols={enabledSymbols}
            disabledSymbols={disabledSymbols}
          />
        </Stack>

        <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage}
                          severity={toastSeverity}/>
      </Card>
    </StackRowLayout>
  )
}

export default FundingRateChart
